import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductTileChildComponent } from "@omni/types/components/product/enums/productTileChildComponent.enum";
import { ProductTileConfig } from "@omni/types/components/product/productTileConfig.type";
import { ProductTileStyle } from "@omni/ui/components/Product/ProductTile/productTile.style";
import { cn } from "@omni/utils/styles";
import { Fragment, ReactNode, Ref, SyntheticEvent, useRef } from "react";
import { useInView } from "react-intersection-observer";

import { Price } from "@/components/Price/price";
import { Label } from "@/components/Product/Label/label";
import { ProductImageContainer } from "@/components/Product/ProductImage/productImage.container";
import Brand from "@/components/Product/ProductTile/components/brand/brand.component";
import ProductTileButton from "@/components/Product/ProductTile/components/button/productTileButton.container";
import CategoryLink from "@/components/Product/ProductTile/components/categoryLink/categoryLink.component";
import { Comparison } from "@/components/Product/ProductTile/components/comparison/comparison.component";
import { ProductTileDescription } from "@/components/Product/ProductTile/components/description/description.component";
import ProductTitle from "@/components/Product/ProductTile/components/title/productTitle.component";
import { WishlistButtonContainer } from "@/components/Wishlist/WishlistButton/WishlistButton.container";
import { CartModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { toTrackingProduct } from "@/services/tracking/mappers/productTileProduct.mapper";
import * as ProductClickTracker from "@/services/tracking/trackers/click/productClick.tracker";
import * as ProductImpressionTracker from "@/services/tracking/trackers/impression/productImpression.tracker";
import { ConditionalWrapper } from "@/utils/helpers";

import { Amount } from "./components/amount/amount.component";
import { DeliveryTypes } from "./components/deliveryTypes/deliveryTypes.container";
import { ProductTileReviews } from "./components/reviews/reviews.component";
import { Stock } from "./components/stock/stock.container";

export type ProductTileComponentProps = {
    product: Product<ProductFieldSet.ProductTile>;
    lazyLoadImage?: boolean;
    allowInstantAddToCart?: boolean;
    extraButtons?: () => ReactNode;
    cart?: CartModel | null;
    extraOnClick?: (e: SyntheticEvent<HTMLButtonElement>) => void;
    analyticsContext?: {
        placement?: number;
        list?: string;
    };
    amount?: number;
    children?: ReactNode;
    className?: string;
    productTileConfig: ProductTileConfig;
    realtimeStockPromise?: Promise<number | null>;
    onClickTracking?: () => void;
    ref?: Ref<HTMLDivElement | HTMLAnchorElement>;
};

const ProductTileComponent = ({
    ref,
    product,
    allowInstantAddToCart,
    extraButtons,
    cart,
    extraOnClick,
    analyticsContext,
    lazyLoadImage,
    children,
    className,
    amount,
    productTileConfig: {
        amountOfFeatures,
        border,
        tilePadding,
        hoverEffect,
        imageRenditions,
        components,
        columnFractions,
        showOnBreakpoint
    },
    onClickTracking,
    realtimeStockPromise
}: ProductTileComponentProps) => {
    const { t } = useGranite();

    const productTileRef = useRef<HTMLDivElement | null>(null);

    const onProductTileClick = () => {
        onClickTracking?.();
        ProductClickTracker.trackProductTile(
            product,
            analyticsContext?.list,
            analyticsContext?.placement
        );
    };

    const trackProductTile = (inView: boolean) => {
        if (!inView) return;

        ProductImpressionTracker.trackProductTile(
            product,
            analyticsContext?.list,
            analyticsContext?.placement
        );
    };

    const { ref: invViewRef } = useInView({
        triggerOnce: true,
        delay: 100,
        onChange: trackProductTile,
        trackVisibility: true
    });

    const productTileComponents = {
        [ProductTileChildComponent.ProductImage]: () => (
            <ProductImageContainer
                className={cn(`product-tile-component--ProductImage`)}
                lazyLoad={lazyLoadImage}
                title={product.title}
                isEcoChequesAccepted={product.ecochecksAcceptedAsPayment}
                medias={product.medias}
                renditions={imageRenditions}
            />
        ),
        [ProductTileChildComponent.PriceSmall]: () => (
            <Price
                price={product.price?.currentPrice ?? 0}
                strikeThroughPrice={product.price?.strikethroughPrice}
                conversionPrice={product.price?.currentConversionPrice}
                conversionUnit={product.price?.conversionUnit}
                size={"md"}
                className={cn(
                    `product-tile-component--PriceSmall`,
                    "tw:flex tw:flex-col tw:items-end tw:justify-start tw:ml-auto"
                )}
            >
                <Price.StrikeThrough />
                <Price.Main />
                <Price.Conversion />
            </Price>
        ),
        [ProductTileChildComponent.PriceLarge]: () => (
            <Price
                price={product.price?.currentPrice ?? 0}
                strikeThroughPrice={product.price?.strikethroughPrice}
                conversionPrice={product.price?.currentConversionPrice}
                conversionUnit={product.price?.conversionUnit}
                size={"lg"}
                className={cn(
                    `product-tile-component--PriceLarge`,
                    "tw:flex tw:flex-col tw:justify-start tw:gap-1",
                    "tw:group-[*]/product-tile__row:first:items-start",
                    "tw:group-[*]/product-tile__row:last:items-end",
                    "tw:group-[*]/product-tile__row:only:items-start"
                )}
            >
                {product.price?.strikethroughPrice && <Price.StrikeThrough />}
                <Price.Main />
                {product.price?.currentConversionPrice &&
                    product.price?.conversionUnit && <Price.Conversion />}
            </Price>
        ),
        [ProductTileChildComponent.DeliveryTypes]: () => (
            <DeliveryTypes
                className={cn(`product-tile-component--DeliveryTypes`)}
                productVisibilityType={product.productVisibilityType}
            />
        ),
        [ProductTileChildComponent.CategoryLink]: () => (
            <CategoryLink
                className={cn(
                    `product-tile-component--CategoryLink`,
                    "productTile__categoryLink small",
                    "tw:max-w-full"
                )}
                superCategoryUrl={product.superCategoryUrl}
                superCategoryName={product.superCategoryName}
            />
        ),
        [ProductTileChildComponent.PromotionLabelSmall]: () =>
            !!product.promoLabel && (
                <Label
                    position={"left"}
                    className={cn(
                        `product-tile-component--PromotionLabelSmall`,
                        "tw:text-xs",
                        "tw:group-[*]/product-tile__row:only:w-full"
                    )}
                    type={"label"}
                >
                    <Label.Entry className={cn("tw:py-1")}>
                        {t(
                            `promotionLabel.${product.promoLabel.promoLabelType}`
                        ).toUpperCase()}
                    </Label.Entry>
                    {product.promoLabel.formattedPromotionValue && (
                        <Label.Entry
                            className={cn(
                                "tw:bg-yellow tw:text-danger tw:py-1"
                            )}
                        >
                            {product.promoLabel.formattedPromotionValue.toUpperCase()}
                        </Label.Entry>
                    )}
                </Label>
            ),
        [ProductTileChildComponent.PromotionLabel]: () =>
            !!product.promoLabel && (
                <Label
                    position={"left"}
                    className={cn(
                        `product-tile-component--PromotionLabel`,
                        "tw:leading-6",
                        "tw:group-[*]/product-tile__row:only:w-full"
                    )}
                    type={"label"}
                >
                    <Label.Entry className={"tw:py-1"}>
                        {t(
                            `promotionLabel.${product.promoLabel.promoLabelType}`
                        ).toUpperCase()}
                    </Label.Entry>
                    {product.promoLabel.formattedPromotionValue && (
                        <Label.Entry
                            className={cn(
                                "tw:bg-yellow tw:text-danger tw:py-1"
                            )}
                        >
                            {product.promoLabel.formattedPromotionValue.toUpperCase()}
                        </Label.Entry>
                    )}
                </Label>
            ),
        [ProductTileChildComponent.PromotionRibbon]: () =>
            !!product.promoLabel && (
                <Label
                    position={"left"}
                    className={cn(
                        `product-tile-component--PromotionRibbon`,
                        "productTile__promotionRibbon",
                        "tw:-translate-x-3 tw:leading-6 tw:text-xs"
                    )}
                    type={"ribbon"}
                >
                    <Label.Entry className={cn("tw:pl-3 tw:py-2")}>
                        {t(
                            `promotionLabel.${product.promoLabel.promoLabelType}`
                        ).toUpperCase()}
                    </Label.Entry>
                    {product.promoLabel.formattedPromotionValue && (
                        <Label.Entry
                            className={cn(
                                "tw:bg-yellow tw:text-danger tw:py-2"
                            )}
                        >
                            {product.promoLabel.formattedPromotionValue.toUpperCase()}
                        </Label.Entry>
                    )}
                </Label>
            ),
        [ProductTileChildComponent.Title]: () => (
            <ProductTitle
                className={cn(
                    `product-tile-component--Title`,
                    "productTile__title"
                )}
                title={product.title}
            />
        ),
        [ProductTileChildComponent.Button]: () => (
            <ProductTileButton
                className={cn(`product-tile-component--Button`, {
                    "tw:grow": components.length > 1
                })}
                product={product}
                allowInstantAddToCart={!!allowInstantAddToCart}
                extraOnClick={extraOnClick}
                cart={cart}
            />
        ),
        [ProductTileChildComponent.Features]: () => (
            <ProductTileDescription
                className={cn(
                    `product-tile-component--Features`,
                    "productTile__features"
                )}
                items={product.features}
                showItems={true}
                amountOfFeatures={amountOfFeatures}
            />
        ),
        [ProductTileChildComponent.Reviews]: () => (
            <div className={cn("tw:h-6.5 tw:flex tw:items-center")}>
                <ProductTileReviews
                    className={cn(`product-tile-component--Reviews`)}
                    totalReviewCount={product.statistics?.totalReviewCount ?? 0}
                    averageOverallRating={
                        product.statistics?.averageOverallRating ?? 0
                    }
                    overallRatingRange={
                        product.statistics?.overallRatingRange ?? 5
                    }
                />
            </div>
        ),
        [ProductTileChildComponent.WishlistButton]: () => (
            <WishlistButtonContainer
                className={cn(`product-tile-component--WishlistButton`)}
                sku={product.sku}
                withReload={true}
                product={toTrackingProduct(product)}
            />
        ),
        [ProductTileChildComponent.Comparison]: () => (
            <Comparison
                className={cn(`product-tile-component--Comparison`)}
                sku={product.sku}
                isAdditionalService={product.isAdditionalService}
                isBoxArticle={!!product.boxArticle}
            />
        ),
        [ProductTileChildComponent.Brand]: () => (
            <Brand
                className={cn(`product-tile-component--Brand`)}
                brandName={product.brand?.name}
            />
        ),
        [ProductTileChildComponent.ExtraButtons]: extraButtons,
        [ProductTileChildComponent.Amount]: () => (
            <Amount
                className={cn(`product-tile-component--Amount`)}
                amount={amount || 0}
            />
        ),
        [ProductTileChildComponent.Stock]: () =>
            !!realtimeStockPromise && (
                <Stock
                    className={cn(`product-tile-component--Stock`)}
                    stockPromise={realtimeStockPromise}
                />
            )
    };

    const tileGridTemplateColumns = Object.assign(
        new Array(components.length).fill(1),
        columnFractions
    )
        .slice(0, components.length)
        .map((fraction) => `${fraction}fr`)
        .join(" ");

    // ProductTile classen mogen niet uit markup!!
    // -> Tailwind selectors kijken hiernaar
    return (
        <div
            className={ProductTileStyle({
                breakpoint: showOnBreakpoint,
                link: !product.disablePDPLink,
                hover: hoverEffect,
                border: border,
                padding: tilePadding,
                className: className
            })}
            ref={(node) => {
                invViewRef(node);
                productTileRef.current = node;
            }}
        >
            <ConditionalWrapper
                condition={product.disablePDPLink}
                wrapper={(children) => (
                    <div
                        className={cn(
                            "tw:no-underline tw:grow tw:flex tw:flex-col"
                        )}
                        ref={(node) => {
                            if (!ref) return;
                            if (typeof ref === "function") ref(node);
                            else ref.current = node;
                        }}
                    >
                        {children}
                    </div>
                )}
                alternative={(children) => (
                    <a
                        href={product.url}
                        ref={(node) => {
                            if (!ref) return;
                            if (typeof ref === "function") ref(node);
                            else ref.current = node;
                        }}
                        onClick={onProductTileClick}
                        className={
                            "tw:no-underline tw:grow tw:flex tw:flex-col"
                        }
                    >
                        {children}
                    </a>
                )}
            >
                {children}
                <div
                    className={cn(
                        // DONT REMOVE - This class is used to style the via parents
                        "productTile--cols",
                        "tw:grid tw:gap-4 tw:grow tw:w-full"
                    )}
                    style={{
                        gridTemplateColumns: tileGridTemplateColumns
                    }}
                >
                    {components.map((col) => (
                        <div
                            key={col.join("-")}
                            className={cn(
                                // DONT REMOVE - This class is used to style the via parents
                                "productTile--rows",
                                "tw:min-w-0 tw:flex tw:flex-col tw:gap-2 tw:grow"
                            )}
                        >
                            {col.map((row) => (
                                <div
                                    key={row.join("-")}
                                    className={cn(
                                        "tw:group/product-tile__row",
                                        "tw:items-center tw:flex tw:justify-between tw:w-full",
                                        "tw:has-[.product-tile-component--Features]:h-full tw:has-[.product-tile-component--Features]:items-start",
                                        "tw:[&>*:only-child]:w-full",
                                        // DONT REMOVE - This class is used to style the via parents
                                        ...row.map(
                                            (c) => `productTile--row--${c}`
                                        )
                                    )}
                                >
                                    {row.map((component) => (
                                        <Fragment key={component}>
                                            {productTileComponents[
                                                component
                                            ]?.()}
                                        </Fragment>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </ConditionalWrapper>
        </div>
    );
};

export default ProductTileComponent;
